import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  TrendingUp,
  Globe2,
  Building2,
  BriefcaseBusiness,
  DollarSign,
  ArrowUpRight,
  CircleDollarSign,
  Factory,
  Landmark,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";

const stats = [
  {
    value: "65.5%",
    label: "Investment Into Nigeria Captured By The Zones",
    icon: TrendingUp,
  },
  {
    value: "200+",
    label: "Companies Across The Zones",
    icon: Building2,
  },
  {
    value: "45",
    label: "Nationalities Represented",
    icon: Globe2,
  },
  {
    value: "$20B+",
    label: "Investment Portfolio",
    icon: DollarSign,
  },
];

const achievements = [
  {
    title: "Africa’s Most Successful Free Trade Zone",
    text:
      "The Financial Times recognized OGFZA as the most successful free trade zone in Africa.",
    icon: Sparkles,
  },
  {
    title: "Regional Oil & Gas Logistics Hub",
    text:
      "The zones positioned Nigeria as one of the leading oil and gas logistics hubs in Sub-Saharan Africa.",
    icon: Factory,
  },
  {
    title: "Massive Employment Creation",
    text:
      "OGFZA and its operators generated thousands of direct and indirect jobs across the oil and gas value chain.",
    icon: BriefcaseBusiness,
  },
  {
    title: "Strategic Infrastructure Growth",
    text:
      "Public-private partnerships accelerated infrastructure development within Nigeria’s oil and gas free zones.",
    icon: Landmark,
  },
];

const impactPoints = [
  "The zones attracted over 200 companies representing 45 nationalities.",
  "Investments exceeded US$20 billion across the zones.",
  "Over 100,000 jobs were created through direct and indirect activities.",
  "Intels invested approximately US$3.6 billion in joint projects with the Nigerian Ports Authority.",
  "An additional US$4.5 billion was invested in private projects and supporting infrastructure.",
  "Nigeria became recognized as a strategic oil and gas logistics destination in Sub-Saharan Africa.",
];

const TrackRecord = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.2,
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 1,
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 55,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".card-animate").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          y: 45,
          delay: index * 0.05,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="OGFZA Track Record"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/85" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/95 via-[#026B07]/70 to-[#F2AF11]/30" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/45 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/20 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Institutional Performance
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Track Record
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            OGFZA has consistently positioned Nigeria’s Oil and Gas Free Zones
            as globally recognized investment destinations driving industrial
            growth, infrastructure expansion and economic development.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/about"
              className="hover:text-[#F2AF11] transition-colors"
            >
              About Us
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">Track Record</span>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative -mt-24 z-20 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="card-animate bg-white rounded-[28px] p-7 shadow-[0_24px_80px_-45px_rgba(8,37,103,0.5)] border border-slate-200 hover:-translate-y-2 hover:border-[#F2AF11]/60 transition-all duration-500"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#F2AF11]/15 text-[#082567] flex items-center justify-center mb-6 border border-[#F2AF11]/25">
                    <Icon size={25} />
                  </div>

                  <h3
                    className="text-4xl font-black text-[#082567] mb-3"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {item.value}
                  </h3>

                  <p className="text-slate-600 leading-[1.7] text-[15px]">
                    {item.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ACHIEVEMENT STORY */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#026B07]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Institutional Achievement
            </p>

            <h2
              className="text-4xl md:text-5xl font-black leading-tight text-[#082567] mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Nigeria’s Leading Oil & Gas Free Zone Success Story
            </h2>

            <div className="w-24 h-1.5 bg-[#F2AF11] rounded-full mb-7" />

            <div className="space-y-5 text-slate-700 leading-[1.9] text-[16px]">
              <p>
                By 2012, the Oil and Gas Free Zones captured approximately
                65.5% of investment into Nigeria and positioned the country as
                one of the leading oil and gas logistics hubs in Sub-Saharan
                Africa.
              </p>

              <p>
                The Financial Times recognized OGFZA as the most successful free
                trade zone in Africa, reflecting the Authority’s strong
                regulatory structure, investment climate and operational
                efficiency.
              </p>

              <p>
                The zones attracted more than 200 companies representing 45
                nationalities with investments exceeding US$20 billion and
                created over 100,000 jobs across the value chain.
              </p>

              <p>
                Through strategic partnerships and infrastructure investment,
                OGFZA has continued to strengthen Nigeria’s position as a major
                regional investment and logistics destination.
              </p>
            </div>
          </div>

          <div className="relative reveal-up">
            <div className="relative rounded-[34px] overflow-hidden shadow-[0_26px_90px_-55px_rgba(8,37,103,0.65)] border border-slate-200 bg-white p-3">
              <img
                src={sectionBg}
                alt="OGFZA Achievements"
                className="w-full h-[520px] object-cover rounded-[26px]"
              />

              <div className="absolute inset-3 rounded-[26px] bg-gradient-to-t from-[#041018]/75 via-transparent to-transparent" />

              <div className="absolute bottom-8 left-8 right-8 bg-white rounded-[24px] p-6 shadow-xl">
                <p className="text-[#026B07] font-black tracking-[0.18em] uppercase text-[11px] mb-2">
                  Global Recognition
                </p>

                <h3
                  className="text-[#082567] text-2xl font-black leading-tight"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Africa’s Most Successful Free Trade Zone
                </h3>

                <p className="text-slate-600 leading-[1.7] mt-3 text-[15px]">
                  Recognized internationally for performance, investment growth
                  and infrastructure development.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ACHIEVEMENT GRID */}
      <section className="relative py-24 bg-[#FAFBFC]">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#082567_1px,transparent_1px),linear-gradient(to_bottom,#082567_1px,transparent_1px)] bg-[size:70px_70px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl reveal-up mb-14">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Key Accomplishments
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Strategic Results Across Investment & Infrastructure
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {achievements.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="card-animate group relative overflow-hidden rounded-[30px] bg-white p-8 border border-slate-200 hover:-translate-y-2 transition-all duration-500 hover:shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)] hover:border-[#F2AF11]/60"
                >
                  <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-[#F2AF11]/10 blur-3xl opacity-0 group-hover:opacity-100 transition-all duration-500" />

                  <div className="relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-[#082567]/5 text-[#082567] flex items-center justify-center mb-6 border border-[#082567]/10">
                      <Icon size={26} />
                    </div>

                    <h3
                      className="text-2xl font-black text-[#082567] leading-tight mb-4"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {item.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* IMPACT POINTS */}
      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="OGFZA Impact Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.07]"
        />
        <div className="absolute inset-0 bg-white/92" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl reveal-up mb-14">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Economic Impact
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Measurable Investment & Development Outcomes
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {impactPoints.map((item, index) => (
              <div
                key={index}
                className="card-animate bg-white border border-slate-200 rounded-[26px] p-7 hover:-translate-y-2 hover:border-[#F2AF11]/60 hover:shadow-[0_24px_80px_-58px_rgba(8,37,103,0.6)] transition-all duration-500"
              >
                <div className="flex gap-5">
                  <div className="w-12 h-12 rounded-2xl bg-[#F2AF11]/15 text-[#082567] border border-[#F2AF11]/25 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 size={23} />
                  </div>

                  <p className="text-slate-700 leading-[1.8] text-[15.5px]">
                    {item}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTMENT FEATURE */}
      <section className="py-24 bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-[36px] bg-white border border-slate-200 p-8 md:p-12 shadow-[0_26px_90px_-60px_rgba(8,37,103,0.65)]">
            <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/12 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[#026B07]/10 blur-3xl" />

            <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
              <div className="reveal-up">
                <div className="inline-flex items-center gap-3 text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-5">
                  <CircleDollarSign size={18} className="text-[#F2AF11]" />
                  Investment Confidence
                </div>

                <h2
                  className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-5"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  OGFZA Continues To Drive Industrial & Investment Expansion
                </h2>

                <p className="text-slate-700 leading-[1.9] text-[16px] max-w-4xl">
                  Through regulatory modernization, infrastructure support,
                  investor-focused policies and strategic partnerships, OGFZA
                  continues to strengthen Nigeria’s oil and gas free zones as
                  globally competitive investment destinations.
                </p>
              </div>

              <Link
                to="/about/why-ogfz"
                className="reveal-up inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-[#082567] hover:bg-[#F2AF11] hover:text-[#082567] text-white font-black transition-all duration-500 whitespace-nowrap"
              >
                Why Invest In OGFZ
                <ArrowUpRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TrackRecord;